import { useEffect, useState } from "react";
import classNames from "classnames";
import { Avatar } from "@mui/material";
import { PendingPersonIcon } from "../../icons";
import { defaultToEmptyString, getAcronym } from "@gx1/gx-npm-lib";
import { TypographyComponent } from "../typography/typography.component";
import styles from "./username-display.styles.module.scss";

type UsernameDisplayProps = {
  ariaLabel?: string;
  colorMod?: number;
  email?: string;
  iconTitle?: string;
  id?: number;
  invertColor?: boolean;
  isColorInvertedOnHover?: boolean;
  isOwner?: boolean;
  isCreatedBy?: boolean;
  isUnknown?: boolean;
  name?: string;
  rootClassName?: string;
  showEmail?: boolean;
  showEmptyAvatar?: boolean;
  showName?: boolean;
  showOnlyFirstName?: boolean;
  roleDisplayFlexDirection?: string;
  isGreyScaled?: boolean;
  isSmallerIcon?: boolean;
};

const COLOR_CLASSES: string[] = ["zero", "one", "two", "three", "four"];

const getUserIdColorClass = (id = 0, defaultMod = -1): string => {
  const isPositiveNumber = (num: number) => num >= 0;
  const idMod = isPositiveNumber(id) ? id % COLOR_CLASSES.length : -1;
  const mod = isPositiveNumber(defaultMod) ? defaultMod % COLOR_CLASSES.length : idMod;
  return COLOR_CLASSES[mod] || "";
};

/**
 * @deprecated DO NOT USE, DO NOT UPDATE
 */
const UsernameDisplay = ({
  ariaLabel = "",
  colorMod = -1,
  email = "",
  iconTitle = "",
  invertColor = false,
  isColorInvertedOnHover = false,
  isOwner = false,
  isCreatedBy = false,
  isUnknown = false,
  name = "",
  rootClassName = "",
  showEmail = false,
  showEmptyAvatar = false,
  showName = true,
  showOnlyFirstName = false,
  roleDisplayFlexDirection = "row",
  isGreyScaled = false,
  isSmallerIcon = false
}: UsernameDisplayProps) => {
  const [displayName, setDisplayName] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [userColorClass, setUserColorClass] = useState("");
  const flexClass = roleDisplayFlexDirection === "row" ? "flex-row" : "flex-column";

  useEffect(() => {
    let value = "";
    if (!isUnknown) {
      const id = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0);
      value = isGreyScaled ? "grey" : getUserIdColorClass(id, colorMod);
    }
    setUserColorClass(value);
  }, [colorMod, isGreyScaled, isUnknown, name]);

  useEffect(() => {
    const display = defaultToEmptyString(name);
    if (showOnlyFirstName) {
      setDisplayName(display.split(/\s/)[0]);
    } else {
      setDisplayName(display);
    }
  }, [name, showOnlyFirstName]);

  const acronym = getAcronym(name);
  const ownerDisplay = isOwner ? "(Owner)" : "";
  const createdByDisplay = isCreatedBy ? "(Created by)" : "";
  const role = createdByDisplay.length === 0 ? ownerDisplay : createdByDisplay;

  return (
    <div
      {...(ariaLabel && { "aria-label": ariaLabel })}
      className={classNames(
        "gx-user",
        styles.userWrapper,
        rootClassName,
        flexClass,
        userColorClass,
        isUnknown && styles.unknownWrapper,
        invertColor && styles.invert,
        isHovered && isColorInvertedOnHover && styles.hovered,
        isSmallerIcon && styles.xs
      )}
    >
      {(acronym || showEmptyAvatar || isUnknown) && (
        <Avatar
          className={styles.avatar}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {isUnknown ? <PendingPersonIcon title={iconTitle} showSmallerIcon={isSmallerIcon} /> : acronym}
        </Avatar>
      )}
      {Boolean(showEmail) && (
        <div className="gx-userInfo">
          {Boolean(displayName) && Boolean(showName) && (
            <TypographyComponent
              rootClassName={"gx-displayName"}
              color={isUnknown ? "iron" : "carbon"}
              boldness={"medium"}
              styling={"p3"}
              isMasked={true}
            >
              {displayName}
            </TypographyComponent>
          )}
          {Boolean(email) && (
            <TypographyComponent isMasked={true} rootClassName={"gx-email"} color={"iron"} styling={"p4"}>
              {email}
            </TypographyComponent>
          )}
        </div>
      )}
      {!showEmail && Boolean(displayName) && Boolean(showName) && (
        <TypographyComponent isMasked={true} rootClassName="gx-ava-owner" boldness={"medium"} color={"coal"}>
          {roleDisplayFlexDirection === "row" ?
            `${displayName} ${role}`
            : (
              <>
                <TypographyComponent element={"span"} color={"carbon"} boldness={"medium"}>
                  {displayName}
                </TypographyComponent>
                <TypographyComponent rootClassName={"gx-ava-role"} boldness={"medium"} element={"span"} color={"iron"}>
                  {role}
                </TypographyComponent>
              </>
            )}
        </TypographyComponent>
      )}
    </div>
  );
};

export { UsernameDisplay };
